/**
 * @fileoverview Custom hook for checking institution link declaration status
 * Used to block doctors from accessing prescription and training pages
 * until they accept the institution link declaration
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { intl, fetchData, HTTP_STATUS } from 'dmsbasereact';
import { APP_ROUTES } from '../utils/NamedRoutes';
import { useConfirmDialog } from './useConfirmDialog';

/**
 * Hook for checking and enforcing institution link declaration for doctors
 * @param {string} userType - The type of the current user
 * @returns {Object} Object containing declaration status and check function
 */
export const useInstitutionLinkDeclaration = (userType) => {
    const navigate = useNavigate();
    const { showWarning } = useConfirmDialog();
    const [loading, setLoading] = useState(true);
    const [declarationStatus, setDeclarationStatus] = useState(null);
    const [hasFetched, setHasFetched] = useState(false);

    const isDoctor = userType === 'doctor';
    const hasDeclaration = declarationStatus?.institution_link_declaration === true;
    // Only require declaration if: is doctor, hasn't declared, AND we've already fetched the status
    const isDeclarationRequired = isDoctor && !hasDeclaration && hasFetched && !loading;

    const fetchDeclarationStatus = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetchData('/doctor/institution_link_declaration', 'GET', true);
            if (response.status === HTTP_STATUS.OK_200) {
                setDeclarationStatus(response.data?.data || response.data);
            }
        } catch (error) {
            console.error('Error fetching declaration status:', error);
        } finally {
            setLoading(false);
            setHasFetched(true);  // Mark as fetched only after API call completes
        }
    }, []);

    useEffect(() => {
        if (isDoctor) {
            // Reset states when becoming a doctor to ensure fresh fetch
            setHasFetched(false);
            setDeclarationStatus(null);
            fetchDeclarationStatus();
        } else if (userType !== null) {
            // Non-doctors don't need declaration check (only if userType is known)
            setLoading(false);
            setHasFetched(true);
        }
        // If userType is null, keep loading=true until we know the user type
    }, [isDoctor, userType, fetchDeclarationStatus]);

    /**
     * Shows a modal explaining why the action is blocked and redirects to profile settings
     * @returns {Promise<void>}
     */
    const showDeclarationRequiredModal = useCallback(async () => {
        const message = `${intl.get('DeclarationRequiredMessage').d(
            'Before you can access this feature, you need to accept the Institution Link Declaration in your profile settings.'
        )}\n\n${intl.get('DeclarationRequiredReason').d(
            'This is required because your registration was created before this requirement was implemented.'
        )}`;

        await showWarning({
            title: intl.get('DeclarationRequiredTitle').d('Declaration Required'),
            message,
            confirmText: 'Understood',
            confirmTextDefault: intl.get('Understood').d('Understood')
        });

        navigate(APP_ROUTES.ProfileSettings);
    }, [navigate, showWarning]);

    /**
     * Checks if the declaration is required and shows the modal if so
     * Returns true if the user can proceed, false if blocked
     * @returns {Promise<boolean>}
     */
    const checkDeclarationAndProceed = useCallback(async () => {
        if (!isDoctor) {
            return true;
        }

        if (loading) {
            await new Promise(resolve => setTimeout(resolve, 500));
            return checkDeclarationAndProceed();
        }

        if (hasDeclaration) {
            return true;
        }

        await showDeclarationRequiredModal();
        return false;
    }, [isDoctor, loading, hasDeclaration, showDeclarationRequiredModal]);

    /**
     * Blocks page access if declaration is required
     * Should be called in useEffect on protected pages
     */
    const blockIfRequired = useCallback(() => {
        if (isDeclarationRequired) {
            showDeclarationRequiredModal();
        }
    }, [isDeclarationRequired, showDeclarationRequiredModal]);

    return {
        loading,
        hasDeclaration,
        isDeclarationRequired,
        checkDeclarationAndProceed,
        blockIfRequired,
        showDeclarationRequiredModal
    };
};

export default useInstitutionLinkDeclaration;
